import { KEEP_PLACE } from "@/lib/search";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { MedicalRecordsPanel } from "@/components/app/hnp/medical-records-panel";
import { EntityNotesPanel } from "@/components/app/entity-notes/entity-notes-panel";
import { orpc } from "@/lib/orpc";
import { useCan, useFeature } from "@/lib/policy";
import { RecordTabs } from "@/components/app/patterns/record-tabs";
import { LeadBillingChartCard } from "./-components/lead-billing-chart-card";
import { LeadConversationsCard } from "./-components/lead-conversations-card";
import { ConvertedTabNotice } from "@/routes/_authenticated/_crm/crm/leads/-components/converted-tab-notice";
import { LeadConvertedBanner } from "./-components/lead-converted-notice";
import { LeadPendingBanner } from "./-components/lead-pending-banner";
import { chartNoticeFor, LeadChartNotice } from "./-components/lead-chart-notice";
import type { ConvertMode } from "./-components/lead-convert-choice";
import { LeadConvertDialog } from "./-components/lead-convert-dialog";
import { LeadDetailHeader } from "./-components/lead-detail-header";
import { LeadDocumentsCard } from "./-components/lead-documents-card";
import { LeadInfoCards } from "./-components/lead-info-cards";
import { LeadNotesActions } from "./-components/lead-notes-actions";
import { LeadMoveInCard } from "./-components/lead-move-in-card";
import { LeadPacketsCard } from "./-components/lead-packets-card";
import { AdmissionPacketSection } from "@/components/app/admission-packet/packet/packet-section";
import { PhysicianDocPanel } from "@/components/app/physician-docs/physician-doc-panel";
import { LeadScoreCard } from "./-components/lead-score-card";
import { LeadStatusRow } from "./-components/lead-status-row";
import { LeadToursCard } from "./-components/lead-tours-card";
import { LeadEventsCard } from "./-components/lead-events-card";
import { InstitutionsCard } from "@/routes/_authenticated/_care/residents/$residentId/-components/care-network/institutions-card";
import { PharmaciesCard } from "@/routes/_authenticated/_care/residents/$residentId/-components/care-network/pharmacies-card";
import { ProvidersCard } from "@/routes/_authenticated/_care/residents/$residentId/-components/providers-card";
import { LeadContactsSection } from "./-components/records/lead-contacts-section";
import { LeadEmergencyContactsSection } from "./-components/records/lead-emergency-contacts-section";
import { LeadInsuranceSection } from "./-components/records/lead-insurance-section";
import { LeadPoaSection } from "./-components/records/lead-poa-section";
import { LeadWealthCards } from "./-components/records/lead-wealth-cards";
import { useZone } from "@/lib/facility/zone";

const TABS = [
  { value: "info", label: "Info" },
  { value: "move-in", label: "Move-in" },
  { value: "packet", label: "Admission packet" },
  { value: "physician", label: "Physician POC" },
  { value: "contacts", label: "Contacts" },
  { value: "tours", label: "Tours" },
  { value: "events", label: "Events" },
  { value: "providers", label: "Providers" },
  { value: "financial", label: "Financial" },
  { value: "documents", label: "Documents" },
  { value: "hnp", label: "Health & needs" },
  { value: "notes", label: "Notes" },
] as const;
type Tab = (typeof TABS)[number]["value"];

export const Route = createFileRoute("/_authenticated/_crm/crm/leads/$leadId")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab; convert?: ConvertMode } => ({
    tab: TABS.some((tab) => tab.value === search.tab) ? (search.tab as Tab) : undefined,
    convert:
      search.convert === "move-in" || search.convert === "pending" ? search.convert : undefined,
  }),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.crm.leads.get.queryOptions({ input: { leadId: params.leadId } }),
    ),
  component: LeadDetailPage,
});

function LeadDetailPage() {
  const { leadId } = Route.useParams();
  const { tab: linkedTab, convert: linkedConvert } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: lead } = useSuspenseQuery(orpc.crm.leads.get.queryOptions({ input: { leadId } }));
  const canWrite = useCan("crm:manage", lead.facilityId);
  // The physician's documents are clinical on either side of conversion, so
  // they ask for the permission the chart asks for, not the CRM's.
  const canWriteClinical = useCan("resident.clinical:update", lead.facilityId);
  const timezone = useZone(lead.facilityId);
  const tabNavigate = Route.useNavigate();
  const tab: Tab = linkedTab ?? "info";
  const setTab = (next: Tab) =>
    void tabNavigate({
      search: (prev) => ({ ...prev, tab: next === "info" ? undefined : next }),
      ...KEEP_PLACE,
    });
  // Both tabs read procedures behind a feature; a building without it would
  // open the tab onto an error rather than onto nothing to do.
  const hasAdmissions = useFeature("admissions", lead.facilityId);
  const hasPhysicianDocs = useFeature("care-plans", lead.facilityId);
  const hasEvents = useFeature("crm-events", lead.facilityId);
  const tabs = TABS.filter(
    (entry) =>
      (entry.value !== "packet" || hasAdmissions) &&
      (entry.value !== "physician" || hasPhysicianDocs) &&
      (entry.value !== "events" || hasEvents),
  );
  const [converting, setConverting] = useState<{ mode: ConvertMode | null } | null>(
    linkedConvert ? { mode: linkedConvert } : null,
  );
  // Once, as the page opens: a lead whose work now lives on a chart says so before anybody types.
  const [notice, setNotice] = useState(() => (linkedConvert ? null : chartNoticeFor(lead)));
  const editable = canWrite && !lead.locked;
  // Converted or pending, the lead is read-only and its work lives on this chart.
  const converted = lead.locked;
  const chartId =
    lead.status === "pending_move_in" ? lead.pendingResidentId : lead.convertedToResidentId;
  const owner = { kind: "lead", id: lead.id } as const;

  return (
    <div className="flex flex-col gap-5">
      <LeadDetailHeader
        lead={lead}
        timezone={timezone}
        onConvert={(mode) => setConverting({ mode: mode ?? null })}
        tabs={<RecordTabs label="Lead sections" tabs={tabs} value={tab} onChange={setTab} />}
      />

      {lead.status === "converted" ? <LeadConvertedBanner lead={lead} canWrite={canWrite} /> : null}
      {lead.status === "pending_move_in" ? (
        <LeadPendingBanner lead={lead} canWrite={canWrite} />
      ) : null}

      {/* The breakdown lives on Info; the other tabs keep the tier and score in the header. */}
      {tab === "info" ? (
        <div className="space-y-6">
          <LeadScoreCard score={lead.score} tier={lead.tier} factors={lead.scoreFactors} />
          <div>
            <LeadStatusRow
              lead={lead}
              canEdit={editable}
              onConvert={(mode) => setConverting({ mode: mode ?? null })}
            />
            <LeadInfoCards key={lead.updatedAt.toString()} lead={lead} canEdit={editable} />
          </div>
          <LeadPoaSection leadId={lead.id} canWrite={editable} />
          <LeadEmergencyContactsSection leadId={lead.id} canWrite={editable} />
        </div>
      ) : null}
      {tab === "move-in" ? (
        converted ? (
          <ConvertedTabNotice what="Move-in steps" residentId={chartId} />
        ) : (
          <LeadMoveInCard
            leadId={lead.id}
            facilityId={lead.facilityId}
            canWrite={canWrite}
            onNavigateTab={(next) => {
              if (TABS.some((entry) => entry.value === next)) setTab(next as Tab);
            }}
          />
        )
      ) : null}
      {/* The resident's own section for an enquiry. The packet started here is the one the resident
          inherits at conversion, so admission is done once. */}
      {tab === "packet" ? (
        converted ? (
          <ConvertedTabNotice what="Admission packets" residentId={chartId} />
        ) : (
          <AdmissionPacketSection
            leadId={lead.id}
            facilityId={lead.facilityId}
            canWrite={editable}
          />
        )
      ) : null}
      {/* The order the physician signs so that somebody MAY move in, and the plan of care handed
          over at the same visit. Started here, they are the resident's own documents after
          conversion, signature and all. */}
      {tab === "physician" ? (
        converted ? (
          <ConvertedTabNotice what="Physician documents" residentId={chartId} />
        ) : (
          <div className="space-y-6">
            <PhysicianDocPanel
              kind="order"
              owner={{ leadId: lead.id }}
              facilityId={lead.facilityId}
              canWrite={canWriteClinical && !lead.locked}
            />
            <div className="border-t pt-6">
              <PhysicianDocPanel
                kind="plan"
                owner={{ leadId: lead.id }}
                facilityId={lead.facilityId}
                canWrite={canWriteClinical && !lead.locked}
              />
            </div>
          </div>
        )
      ) : null}
      {tab === "contacts" ? <LeadContactsSection leadId={lead.id} canWrite={editable} /> : null}
      {tab === "tours" ? <LeadToursCard leadId={lead.id} canWrite={editable} /> : null}
      {tab === "events" ? <LeadEventsCard leadId={lead.id} canWrite={editable} /> : null}
      {tab === "providers" ? (
        <div className="space-y-6">
          <ProvidersCard owner={owner} facilityId={lead.facilityId} canWrite={editable} />
          <InstitutionsCard owner={owner} facilityId={lead.facilityId} canWrite={editable} />
          <PharmaciesCard owner={owner} facilityId={lead.facilityId} canWrite={editable} />
        </div>
      ) : null}
      {tab === "financial" ? (
        <div className="space-y-6">
          {converted ? null : (
            <LeadBillingChartCard lead={lead} onOpen={() => setConverting({ mode: "pending" })} />
          )}
          <LeadInsuranceSection leadId={lead.id} canWrite={editable} />
          <LeadWealthCards leadId={lead.id} canWrite={editable} />
        </div>
      ) : null}
      {tab === "documents" ? (
        <LeadDocumentsCard leadId={lead.id} facilityId={lead.facilityId} canWrite={editable} />
      ) : null}
      {tab === "hnp" ? (
        converted ? (
          <ConvertedTabNotice what="Health & needs records" residentId={chartId} />
        ) : (
          <MedicalRecordsPanel
            entity={{ entityType: "lead", entityId: lead.id }}
            facilityId={lead.facilityId}
            canWrite={editable}
            releaseForms={
              <LeadPacketsCard
                leadId={lead.id}
                facilityId={lead.facilityId}
                converted={Boolean(lead.convertedToResidentId)}
                canWrite={editable}
              />
            }
          />
        )
      ) : null}
      {tab === "notes" ? (
        converted ? (
          <ConvertedTabNotice what="Notes" residentId={chartId} />
        ) : (
          <div className="space-y-6">
            <EntityNotesPanel
              entityType="lead"
              entityId={lead.id}
              facilityId={lead.facilityId}
              timezone={timezone}
              extraActions={editable ? <LeadNotesActions lead={lead} /> : null}
            />
            <LeadConversationsCard
              leadId={lead.id}
              facilityId={lead.facilityId}
              canWrite={editable}
            />
          </div>
        )
      ) : null}

      {notice ? (
        <LeadChartNotice lead={lead} kind={notice} onClose={() => setNotice(null)} />
      ) : null}
      {converting ? (
        <LeadConvertDialog
          lead={lead}
          initialMode={converting.mode}
          onPendingOpened={() => {
            setConverting(null);
            setNotice("pending");
          }}
          onClose={() => {
            setConverting(null);
            if (linkedConvert)
              void navigate({ search: (prev) => ({ ...prev, convert: undefined }) });
          }}
        />
      ) : null}
    </div>
  );
}
